import type { RefObject } from 'react';
import { useCallback } from 'react';

import { getCurrentTimeFromKeyboardKey } from '../../VideoUpload.utils';

type UseKeyboardShortcutsOptions = {
  startTime: number;
  endTime: number;
  videoRef: RefObject<HTMLVideoElement>;
  onPlayPause: () => void;
  onSeekForward: () => void;
  onSeekBackward: () => void;
  onToggleMuted: () => void;
  onIncreaseVolume: () => void;
  onDecreaseVolume: () => void;
  onToggleFullscreen: () => Promise<void>;
};

const isInteractiveElement = (element: HTMLElement): boolean => {
  const tagName = element.tagName.toLowerCase();
  const role = element.getAttribute('role');

  return tagName === 'button' || role === 'button';
};

export const useKeyboardShortcuts = ({
  startTime,
  endTime,
  videoRef,
  onPlayPause,
  onSeekForward,
  onSeekBackward,
  onToggleMuted,
  onIncreaseVolume,
  onDecreaseVolume,
  onToggleFullscreen,
}: UseKeyboardShortcutsOptions) => {
  const handleKeyDown = useCallback(
    async (event: React.KeyboardEvent<HTMLDivElement>) => {
      const videoElement = videoRef.current;
      if (!videoElement) {
        return;
      }

      switch (event.key) {
        // Playback controls
        case ' ':
          if (isInteractiveElement(event.target as HTMLElement)) {
            break;
          }

          event.preventDefault();
          onPlayPause();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          onSeekBackward();
          break;
        case 'ArrowRight':
          event.preventDefault();
          onSeekForward();
          break;

        // Audio controls
        case 'm':
          event.preventDefault();
          onToggleMuted();
          break;
        case 'ArrowUp':
          event.preventDefault();
          onIncreaseVolume();
          break;
        case 'ArrowDown':
          event.preventDefault();
          onDecreaseVolume();
          break;

        // Display controls
        case 'f':
          event.preventDefault();
          await onToggleFullscreen();
          break;

        // Time navigation
        case 'Home':
          event.preventDefault();
          videoElement.currentTime = 0;
          break;
        case 'End':
          event.preventDefault();
          videoElement.currentTime = endTime;
          break;
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          event.preventDefault();
          videoElement.currentTime = getCurrentTimeFromKeyboardKey({
            key: event.key,
            startTime,
            endTime,
          });
          break;
      }
    },
    [
      endTime,
      onDecreaseVolume,
      onIncreaseVolume,
      onPlayPause,
      onSeekBackward,
      onSeekForward,
      onToggleFullscreen,
      onToggleMuted,
      startTime,
      videoRef,
    ],
  );

  return { handleKeyDown };
};
